import { useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AdBanner, { AdInFeed } from "@/components/feature/AdBanner";
import { useNews } from "@/hooks/useSupabaseData";
import { useAnalytics } from "@/hooks/useAnalytics";

const categories = [
  "Todas",
  "BrasileirÃ£o",
  "Internacional",
  "Mercado da Bola",
  "Entrevista",
  "SeleÃ§Ã£o",
  "Institucional",
  "Categorias de Base",
];

function newsHasCategory(news: { category: string; categories?: string[] | null }, category: string) {
  return news.category === category || Boolean(news.categories?.includes(category));
}

export default function NewsList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(searchParams.get("categoria") || "Todas");
  const [currentPage, setCurrentPage] = useState(1);
  useAnalytics("/noticias");

  const { news: allNews, loading } = useNews({ limit: 100 });

  const filteredNews = activeCategory === "Todas"
    ? allNews
    : allNews.filter((n) => newsHasCategory(n, activeCategory));

  const itemsPerPage = 6;
  const totalPages = Math.ceil(filteredNews.length / itemsPerPage);
  const paginatedNews = filteredNews.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="min-h-screen bg-white relative">
      <Navbar />
      <div className="absolute top-0 left-0 right-0 h-48 md:h-64 bg-gradient-to-b from-alvinegro-black to-white z-0" />
      <main className="pt-20 md:pt-24 pb-12 relative z-10">
        <div className="w-full px-4 md:px-8">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: "InÃ­cio", path: "/" },
              { label: "NotÃ­cias" },
            ]}
          />

          {/* Header */}
          <div className="mb-8">
            <h1 className="font-serif font-bold text-3xl md:text-4xl text-alvinegro-black mb-4">
              NotÃ­cias
            </h1>
            <p className="text-alvinegro-muted text-base">
              Fique por dentro de tudo que acontece com o Glorioso
            </p>
          </div>

          {/* Ad Banner - Top */}
          <div className="mb-6">
            <AdBanner variant="leaderboard" label="Publicidade" />
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setActiveCategory(cat);
                  setCurrentPage(1);
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeCategory === cat
                    ? "bg-alvinegro-black text-white"
                    : "bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* News Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 animate-pulse">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="space-y-3">
                  <div className="aspect-[16/9] bg-alvinegro-light rounded-2xl"></div>
                  <div className="h-5 bg-alvinegro-light rounded w-full"></div>
                  <div className="h-4 bg-alvinegro-light rounded w-3/4"></div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {paginatedNews.map((news, index) => (
                  <div
                    key={news.id}
                    className={`group cursor-pointer ${index === 0 ? "md:col-span-2 lg:col-span-2" : ""}`}
                    onClick={() => navigate(`/noticias/${news.slug}`)}
                  >
                    <div className={`relative rounded-2xl overflow-hidden mb-3 ${index === 0 ? "aspect-[16/9]" : "aspect-[4/3]"}`}>
                      <img
                        src={news.image_url || "https://readdy.ai/api/search-image?query=Football%20match%20action%20professional%20sports%20photography%20dramatic&width=800&height=600&seq=news&orientation=landscape"}
                        alt={news.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <span className="absolute top-3 right-3 bg-alvinegro-black/80 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                        {news.category}
                      </span>
                      {index === 0 && (
                        <div className="absolute bottom-4 left-4 right-4">
                          <h2 className="font-serif font-bold text-xl md:text-2xl text-white leading-tight">
                            {news.title}
                          </h2>
                        </div>
                      )}
                    </div>
                    {index > 0 && (
                      <>
                        <h3 className="font-semibold text-base text-alvinegro-black leading-snug line-clamp-2 group-hover:text-alvinegro-gray transition-colors">
                          {news.title}
                        </h3>
                        <p className="text-alvinegro-muted text-sm mt-1 line-clamp-2">
                          {news.subtitle}
                        </p>
                      </>
                    )}
                    <div className="flex items-center gap-2 mt-2 text-xs text-alvinegro-muted">
                      <span>{news.published_at ? new Date(news.published_at).toLocaleDateString("pt-BR") : ""}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Ad In-Feed entre notÃ­cias e paginaÃ§Ã£o */}
              <div className="my-6 md:my-8">
                <AdInFeed />
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-10 h-10 rounded-full flex items-center justify-center bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border disabled:opacity-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors cursor-pointer ${
                        currentPage === page
                          ? "bg-alvinegro-black text-white"
                          : "bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-10 h-10 rounded-full flex items-center justify-center bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border disabled:opacity-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          )}

          {/* Ad Banner - Bottom */}
          <div className="mt-8">
            <AdBanner variant="medium-rectangle" label="Publicidade" />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
